import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirebaseToken } from "../utils/getFirebaseToken";
import DocumentHeaderForm from "../components/DocumentHeaderForm";
import DocumentLinesTable from "../components/DocumentLinesTable";
import { fetchInventoryBalances } from "../services/erpInventory";
import {
  approveDocument,
  createDocument,
  fetchDocumentById,
  postDocument,
  submitDocument,
  updateDocument,
} from "../services/erpDocuments";
import {
  fetchLocations,
  fetchPartners,
  fetchResourceItems,
  fetchUom,
  fetchWarehouses,
} from "../services/erpMasterData";

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

function emptyHeader() {
  return {
    doc_type: "",
    doc_date: todayDate(),
    posting_date: todayDate(),
    source_warehouse_id: null,
    target_warehouse_id: null,
    source_location_id: null,
    target_location_id: null,
    partner_id: null,
    project_id: null,
    employee_id: null,
    cost_center_id: null,
    currency: "RSD",
    exchange_rate: 1,
    remarks: "",
    status: "DRAFT",
  };
}

export default function DocumentEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [resourceItems, setResourceItems] = useState([]);
  const [uoms, setUoms] = useState([]);

  const [loading, setLoading] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [inventoryMap, setInventoryMap] = useState({});

  const totalAmount = useMemo(() => {
    return lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  }, [lines]);

  const getToken = async () => {
  return await getFirebaseToken();
};

  const loadMasterData = async () => {
    setMasterLoading(true);
    try {
      const token = await getToken();
      const inv = await fetchInventoryBalances(token);

const map = {};
(inv.rows || []).forEach((r) => {
  const key = `${r.resource_item_id}_${r.warehouse_id}_${r.location_id}`;
  map[key] = Number(r.qty_on_hand || 0);
});

setInventoryMap(map);
      const [wh, loc, prt, items, unitRows] = await Promise.all([
        fetchWarehouses(token),
        fetchLocations(token),
        fetchPartners(token),
        fetchResourceItems(token),
        fetchUom(token),
      ]);

      setWarehouses(wh.rows || wh.data || wh || []);
      setLocations(loc.rows || loc.data || loc || []);
      setPartners(prt.rows || prt.data || prt || []);
      setResourceItems(items.rows || items.data || items || []);
      setUoms(unitRows.rows || unitRows.data || unitRows || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to load master data.");
    } finally {
      setMasterLoading(false);
    }
  };

  const resourceMap = useMemo(() => {
  const map = {};
  resourceItems.forEach((r) => {
    map[r.id] = r;
  });
  return map;
}, [resourceItems]);

  const loadDocument = async () => {
    if (isNew) return;

    setLoading(true);
    try {
      const token = await getToken();
      const data = await fetchDocumentById(id, token);

      const doc = data.data || data;
      const h = doc.header || doc;

setHeader({
  ...h,
  doc_date: toDateInputValue(h.doc_date),
  posting_date: toDateInputValue(h.posting_date),
});
      setLines(doc.lines || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to load document.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasterData();
    loadDocument();
  }, [id]);

  const saveDocument = async () => {
    setSaving(true);
    setError("");
    try {
      const token = await getToken();

      const payload = {
        ...header,
        lines,
      };

      let result;
      if (isNew) {
        result = await createDocument(payload, token);
      } else {
        result = await updateDocument(id, payload, token);
      }

      const savedId = result?.id || result?.data?.id || id;

      if (isNew && savedId) {
        navigate(`/erp/documents/${savedId}`);
      } else {
        await loadDocument();
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to save document.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = await getToken();
      await submitDocument(id, token);
      await loadDocument();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to submit document.");
    }
  };

  const handleApprove = async () => {
    try {
      const token = await getToken();
      await approveDocument(id, token);
      await loadDocument();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to approve document.");
    }
  };

  const handlePost = async () => {
    try {
      const token = await getToken();
      await postDocument(id, token);
      await loadDocument();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to post document.");
    }
  };

  function toDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

  const isDraft = !header?.status || header?.status === "DRAFT";
const isSubmitted = header?.status === "SUBMITTED";
const isApproved = header?.status === "APPROVED";
const isPosted = header?.status === "POSTED";

const isLocked = ["APPROVED", "POSTED", "CANCELLED"].includes(header?.status);
// ili strože:
// const isLocked = ["SUBMITTED", "APPROVED", "POSTED", "CANCELLED"].includes(header?.status);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            {isNew ? "New ERP Document" : `Document ${header.doc_no || id}`}
          </h1>
          <p className="text-slate-600">
            Status: <span className="font-medium">{header.status || "DRAFT"}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={saveDocument}
            disabled={saving || isLocked}
            className="px-4 py-2 rounded-lg bg-black text-white disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>

          {!isNew && isDraft ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 rounded-lg border"
            >
              Submit
            </button>
          ) : null}

          {!isNew && isSubmitted ? (
            <button
              type="button"
              onClick={handleApprove}
              className="px-4 py-2 rounded-lg border"
            >
              Approve
            </button>
          ) : null}

          {!isNew && isApproved ? (
            <button
              type="button"
              onClick={handlePost}
              className="px-4 py-2 rounded-lg border"
            >
              Post
            </button>
          ) : null}
        </div>
      </div>

      {isLocked ? (
  <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-800 px-4 py-3">
    This document is locked because it is {header.status}.
    To correct it, create a reversal or adjustment document.
  </div>
) : null}

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      {loading || masterLoading ? (
        <div className="bg-white rounded-xl shadow-sm border p-6">Loading...</div>
      ) : (
        <>
          <DocumentHeaderForm
  value={header}
  onChange={setHeader}
  warehouses={warehouses}
  locations={locations}
  partners={partners}
  disabled={isLocked}
/>

          <DocumentLinesTable
  lines={lines}
  onChange={setLines}
  resourceItems={resourceItems}
  uoms={uoms}
  resourceMap={resourceMap}
  inventoryMap={inventoryMap}
  header={header}
  disabled={isLocked}
/>

          <div className="bg-white rounded-xl shadow-sm border p-4 mt-4">
            <div className="flex justify-end">
              <div className="text-right">
                <div className="text-sm text-slate-500">Total Amount</div>
                <div className="text-2xl font-bold">
                  {Number(totalAmount || 0).toFixed(4)} {header.currency || "RSD"}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}